import UserManagerClient from '@service/UserManagerClient';
import { User, UserProfile } from 'oidc-client-ts';

export const featAutomationAuthBypass =
  import.meta.env.VITE_FEAT_AUTOMATION_AUTH_BYPASS === 'true';

// Automation is only wired up on SIT/UAT — never local/dev builds, never production.
const ALLOWED_ENVS = ['sit', 'uat'];
const isAllowedEnv = ALLOWED_ENVS.includes(
  (import.meta.env.VITE_ENV as string).toLowerCase(),
);

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    );
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * bravo-agency-service validates this token as a normal Bearer JWT (confirmed
 * against SIT/UAT via `GET /agency/v1/employee`) — no backend bypass needed.
 * The missing piece on the FE is that `serviceFactory.ts` reads the access
 * token from `UserManagerClient.getUser()` (oidc-client-ts's own session
 * store), not from the plain `sessionStorage.TOKEN` key `TokenStorageBridge`
 * mirrors it to. So the injected token must be stored as a real oidc-client-ts
 * `User` via the library's own `storeUser()` API, not just raw storage keys.
 */
export async function applyAutomationSessionIfEligible(): Promise<void> {
  if (!featAutomationAuthBypass || !isAllowedEnv) return;

  const nik = sessionStorage.getItem('AUTOMATION_NIK')?.trim();
  const accessToken = sessionStorage.getItem('TOKEN');
  if (!nik || !accessToken) return;

  const accessClaims = decodeJwtPayload(accessToken);
  // Only `exp` is load-bearing: it is what tells us the session is still usable,
  // and oidc-client-ts needs it to decide the user is not expired.
  const exp = accessClaims?.exp;
  if (typeof exp !== 'number' || Date.now() >= exp * 1000) return;

  const idToken = sessionStorage.getItem('ID_TOKEN');
  const idClaims = idToken ? decodeJwtPayload(idToken) : null;

  // A token-exchange access token can come back without the identity claims a
  // login token carries (`sub` in particular). That is not a reason to refuse the
  // session: the access token is still a valid, signed Bearer the backend accepts.
  // So the *profile* is assembled from whatever is available — never by rewriting
  // the token, which would break its signature — and the id_token is used for
  // claims only, never as the Bearer (the gateway rejects an id_token from the
  // browser).
  const claims: Record<string, unknown> = {
    ...(idClaims ?? {}),
    ...(accessClaims ?? {}),
    // `sub` identifies the session to oidc-client-ts. Fall back to the id_token's,
    // then to the NIK the automation asked to impersonate.
    sub: accessClaims?.sub ?? idClaims?.sub ?? nik,
    exp,
  };
  if (!claims.iss && idClaims?.iss) claims.iss = idClaims.iss;
  if (!claims.preferred_username) claims.preferred_username = nik;

  const user = new User({
    access_token: accessToken,
    refresh_token: sessionStorage.getItem('REFRESH_TOKEN') ?? undefined,
    id_token: idToken ?? undefined,
    token_type: 'Bearer',
    profile: claims as unknown as UserProfile,
    expires_at: exp,
  });

  await UserManagerClient.storeUser(user);
}
